/**
 * @experimental
 * We try to keep one routing structure for all features, while being able to support dynamic tokens and matching capabilities.
 *
 * Example usage for pattern matching:
 * <Route path={myRouting.forWorkspace().resources().root()} />
 *
 * Example usage for navigation:
 * <Link to={myRouting.forWorkspace('production_team').contacts().detail('123')} />
 *
 * Later we will maybe create a service that is able to navigate to specific path, while being able to substitute paths with current values, so
 * /app/:app_id/contacts/:id can be navigated to with /app/production_team/contacts/123 and /app/:app_id/contacts/456 can be navigated to with /app/production_team/contacts/456
 *
 */
export const myRouting = {
  forWorkspace(workspaceId = ':app_id') {
    return {
      root: () => `/app/${workspaceId}`,
      resources: () => {
        const base = `/app/${workspaceId}/resources`;
        return {
          root: () => base,
          item: (resourceTypeId: string | number = ':resourceTypeId') =>
            `${base}/${resourceTypeId}`
        };
      },
      contacts: () => {
        const base = `/app/${workspaceId}/contacts`;
        return {
          root: () => base,
          detail: (contactId: string | number = ':id') =>
            `${base}/${contactId}`,
          tab: (contactId: string | number = ':id', tab: string = ':tab') =>
            `${base}/${contactId}/${tab}`
        };
      },
      settings: () => {
        const base = `/app/settings/team/${workspaceId}`;
        return {
          root: () => base,
          rateCards: () => `${base}/rate-cards`
        };
      },
      productions: () => {
        const base = `/app/${workspaceId}/productions`;
        const detailPath = (productionId: string | number = ':id') =>
          `${base}/${productionId}`;
        return {
          root: () => base,
          type: (productionTypeId: string | number = ':productionTypeId') =>
            `${base}/type/${productionTypeId}`,
          detail: detailPath,
          resourcing: (productionId: string | number = ':id') => ({
            root: () => `${detailPath(productionId)}/resourcing`,
            tab: (tabId: string = ':tabId') =>
              `${detailPath(productionId)}/resourcing/${tabId}`
          })
        };
      },
      shortlists: () => ({
        root: () => `/app/${workspaceId}/shortlists`,
        detail: (shortlistId: string | number = ':id') =>
          `/app/${workspaceId}/shortlists/${shortlistId}`
      }),
      callouts: () => ({
        root: () => `/app/${workspaceId}/callouts`,
        detail: (calloutId: string | number = ':id') =>
          `/app/${workspaceId}/callouts/${calloutId}`,
        publicCallout: (calloutSlug: string | number = ':id') =>
          `/app/${workspaceId}/p/callouts/${calloutSlug}`
      }),
      bookings: () => ({
        root: () => `/app/${workspaceId}/bookings`,
        type: (bookingTypeId: string | number = ':bookingTypeId') =>
          `/app/${workspaceId}/bookings/type/${bookingTypeId}`,
        detail: (bookingId: string | number = ':id') =>
          `/app/${workspaceId}/bookings/${bookingId}`
      }),
      callsheets: () => ({
        root: () => `/app/${workspaceId}/callsheets`,
        detail: (callsheetId: string | number = ':id') =>
          `/app/${workspaceId}/callsheets/${callsheetId}`
      }),
      reconciliations: () => ({
        root: () => `/app/${workspaceId}/reconciliations`
      })
    };
  }
} as const;

export type AppRouting = typeof myRouting;
